<template>
  <pu-map
    ref="map"
    v-bind="puMapProps"
    class="pu-map"
    :visible-marker="visibleMarker"
    :fly-to="flyTo"
    @marker-selected="$emit('marker-selected', $event)"
    @marker-clicked="markerClicked"
  >
    <template #popup="{ markerData }">
      <div class="pu-infowindow">
        <slot name="popup" :marker-data="markerData" />
      </div>
    </template>
    <template #dynamicPopup>
      <div class="pu-infowindow">
        <slot name="dynamicPopup" />
      </div>
    </template>
  </pu-map>
</template>

<script lang="ts">
import { getPointFromStringOrDefault } from '~/utility/geo/latLng'
import { priceToMoneyType } from '~/utility/money'
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { MapCampsite } from '../../domain/MapsCampsite'
import { PuMapProps } from '../pu-map/PuMapProps'
import { IconMarker, MarkerType, PriceMarker } from '../pu-map/types'
import CampsitesMapBase from './CampsitesMapBase'
import { CampsitesMapProps } from './CampsitesMapProps'

@Component({
  mixins: [CampsitesMapBase],
  emits: ['marker-selected'],
  setup() {
    const currencyStore = useCurrencyStore(usePinia())
    const seenCampsitesStore = useSeenCampsitesStore(usePinia())
    return {
      currencyStore,
      seenCampsitesStore,
    }
  },
})
class CampsitesMap extends Vue implements CampsitesMapProps {
  currencyStore: ReturnType<typeof useCurrencyStore>
  seenCampsitesStore: ReturnType<typeof useSeenCampsitesStore>

  @Prop({ required: true })
  campsites: MapCampsite[]

  @Prop({ default: MarkerType.icon })
  markersType: MarkerType

  @Prop({ default: undefined })
  flyTo?: boolean

  @Prop()
  showSeenCampsites: boolean

  get markers() {
    // logger('CampsitesMap.markers', this.campsites)
    return toRaw(this.campsites).map((campsite) => {
      if (!campsite.price || this.markersType === MarkerType.icon) {
        return this.getIconMarker(campsite)
      }
      return this.getPriceMarker(campsite)
    })
  }

  private getMarkerBase(campsite: MapCampsite) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for campsite position
    const { lat, lng } = campsite.position
      ? campsite.position
      : getPointFromStringOrDefault(campsite.point)
    return {
      campsiteId: campsite.id,
      data: campsite,
      name: campsite.name,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for campsite position
      position: { lat, lng },
      slug: campsite.slug,
      seenMarkers: this.showSeenCampsites
        ? this.seenCampsitesStore.seenCampsites
        : undefined,
    }
  }

  private getIconMarker(campsite: MapCampsite): IconMarker {
    return {
      ...this.getMarkerBase(campsite),
      type: MarkerType.icon,
    }
  }

  private getPriceMarker(campsite: MapCampsite): PriceMarker {
    const convertedPrice = this.currencyStore.convert(
      priceToMoneyType(campsite.price),
      this.currencyStore.currentCurrencyCode,
    )
    return {
      ...this.getMarkerBase(campsite),
      type: MarkerType.price,
      price: convertedPrice,
    }
  }

  get puMapProps(): PuMapProps {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
    const centre =
      this.markers.length === 1
        ? this.markers[0]?.position
        : this.centre || this.circle
    return {
      centreOnCircle: false,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      initialZoom: this.initialZoom,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      centre,
      markers: this.markers,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      initialBounds: this.initialBounds,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      circle: this.circle,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      useClustering: this.useClustering,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Add proper types for inherited props from CampsitesMapBase
      zoomControl: this.zoomControl,
    }
  }

  markerClicked(marker) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Add proper types for marker
    this.seenCampsitesStore.add(marker.slug)
    this.$emit('marker-clicked', marker)
  }
}

export default toNative(CampsitesMap)
</script>
<style scoped>
.pu-map {
  width: 100%;
  height: 100%;
}
</style>
